import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import WebGLVectorLayer from 'ol/layer/WebGLVector';
import VectorSource from 'ol/source/Vector';
import TileWMS from 'ol/source/TileWMS';
import { Style, Fill, Stroke } from 'ol/style';

/** 안정적인 참조를 위한 상수 스타일 — 매 프레임 새 객체 생성을 방지 */
const PANEL_DEFAULT_STYLE = new Style({
  fill: new Fill({ color: 'rgba(30,136,229,0.10)' }),
  stroke: new Stroke({ color: '#1E88E5', width: 2.5 })
});

/** WebGLVectorLayer용 flat 스타일 (GPU 가속, 선택 하이라이트 미지원) */
const PANEL_WEBGL_STYLE = {
  'fill-color': [
    'case',
    ['==', ['get', 'featureType'], 'Array'],
    'rgba(255,152,0,0.14)',
    ['==', ['get', 'featureType'], 'String'],
    'rgba(30,136,229,0.10)',
    ['==', ['get', 'featureType'], 'Module'],
    'rgba(0,137,123,0.16)',
    'rgba(0,0,0,0)'
  ],
  'stroke-color': [
    'case',
    ['==', ['get', 'featureType'], 'Array'],
    '#FF9800',
    ['==', ['get', 'featureType'], 'String'],
    '#1E88E5',
    ['==', ['get', 'featureType'], 'Module'],
    '#00897B',
    '#000'
  ],
  'stroke-width': [
    'case',
    ['==', ['get', 'featureType'], 'Array'],
    3,
    ['==', ['get', 'featureType'], 'String'],
    2.5,
    ['==', ['get', 'featureType'], 'Module'],
    2,
    1
  ]
};

export function getPanelPlanningWmsUrl() {
  const base = process.env.VUE_APP_GEO_SERVER_URL || 'https://map.ssolvit.com';
  return `${base}/wms`;
}

export function createSatelliteTileLayer() {
  return new TileLayer({
    className: 'dotman.solar.map.satellite',
    visible: true,
    source: new TileWMS({
      url: getPanelPlanningWmsUrl(),
      params: {
        FORMAT: 'image/png',
        VERSION: '1.1.1',
        tiled: true,
        LAYERS: 'solar-plant:Satellite',
        SRS: 'EPSG:4326',
        exceptions: 'application/vnd.ogc.se_inimage'
      }
    })
  });
}

export function createSiteImageTileLayer() {
  return new TileLayer({
    visible: true,
    source: new TileWMS({
      url: getPanelPlanningWmsUrl(),
      params: {
        FORMAT: 'image/png',
        VERSION: '1.1.1',
        tiled: true,
        LAYERS: 'solar-plant:site_image_grp_layer',
        SRS: 'EPSG:4326',
        exceptions: 'application/vnd.ogc.se_inimage'
      }
    })
  });
}

/** 뷰포트 밖(px)까지 벡터를 그릴 여유. 2000이면 대형 사이트 전체가 매 프레임 repaint 대상 */
export const PANEL_VECTOR_RENDER_BUFFER = 100;

export function createPanelVectorLayer(vectorSource) {
  return new VectorLayer({
    source: vectorSource,
    style: PANEL_DEFAULT_STYLE,
    declutter: true,
    renderBuffer: PANEL_VECTOR_RENDER_BUFFER
  });
}

/** WebGLVectorLayer — GPU 가속, 수만 개 Feature도 60fps. 선택 하이라이트 미지원. */
export function createPanelWebGLVectorLayer(vectorSource) {
  return new WebGLVectorLayer({
    source: vectorSource,
    style: PANEL_WEBGL_STYLE,
    declutter: true
  });
}

/**
 * 패널 플래닝 기본 레이어 스택
 * @param {object} [options]
 * @param {'canvas'|'webgl'} [options.renderer='canvas'] — 벡터 렌더러 선택
 * @returns {{ vectorSource, vectorLayer, satelliteTileLayer, siteImageTileLayer, layers }}
 */
export function createPanelPlanningLayerStack(options = {}) {
  const { renderer = 'canvas' } = options;
  const vectorSource = new VectorSource();
  const vectorLayer =
    renderer === 'webgl'
      ? createPanelWebGLVectorLayer(vectorSource)
      : createPanelVectorLayer(vectorSource);
  const satelliteTileLayer = createSatelliteTileLayer();
  const siteImageTileLayer = createSiteImageTileLayer();
  return {
    vectorSource,
    vectorLayer,
    satelliteTileLayer,
    siteImageTileLayer,
    layers: [satelliteTileLayer, siteImageTileLayer, vectorLayer]
  };
}
